import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import {
  Box,
  Chip,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import SearchIcon from "../../../assets/icons/search-icon.png";
import GlobalButton from "../../../components/button";
import CustomRangePicker from "../../../components/dateRange";
import CustomDropDown from "../../../components/select";
import { setCurrentPage } from "../../../lib/redux/slices/contractPaginateSlice";
import {
  setLeadEndDate,
  setLeadSaleStatusValue,
  setLeadSearchValue,
  setLeadSelectedAgents,
  setLeadStartDate,
} from "../../../lib/redux/slices/searchKeysSlice";
import { getAllAgents } from "../../../services/AgentApi";
import { handleOpenSnackBar } from "../../../utils/helper";

const controlStyles = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "50px",
    backgroundColor: "#ffffff",
    fontSize: "0.875rem",
    minHeight: 40,
    "& .MuiOutlinedInput-input": {
      padding: "8px 10px !important",
    },
    "& .MuiSelect-select": {
      padding: "8px 10px !important",
      display: "flex",
      alignItems: "center",
      minHeight: "24px !important",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "1px solid",
      borderColor: "primary.main",
    },
    "&:hover .MuiOutlinedInput-notchedOutline": {
      border: "1px solid",
      borderColor: "primary.main",
    },
    "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
      border: "1px solid",
      borderColor: "primary.main",
    },
  },
};

const chipWrapStyle = {
  display: "flex",
  flexWrap: "wrap",
  gap: 0.5,
  alignItems: "center",
};

const SaleStatus = [
  "Lead",
  "Contract Sent",
  "Sale",
  "Provisioning",
  "Live",
  "Future Live",
  "Delayed Live",
  "Rework",
  "Winback",
  "Cancel Pre-Live - In Contract",
  "Cancel Pre-Live - Better price",
  "Cancel Pre-Live - Change of mind or Not interested",
  "Cancel Pre-Live - No reason disclosed",
  "Cancel Pre Live - Incorrect Product Sold",
  "Cancel Pre-Live - Incompatible product",
  "Delete Lead",
  "Duplicate lead",
  "Test",
];

const SearchTab = ({ allContracts, onDateFilter, handleReset }) => {
  const dispatch = useDispatch();
  const searchKeys = useSelector((state) => state.searchKeysReducer.searchKeys);
  const [allAgents, setAllAgents] = useState([]);

  useEffect(() => {
    fetchAgents();
  }, []);

  const fetchAgents = async () => {
    try {
      const response = await getAllAgents();
      if (response?.data?.status === "success") {
        setAllAgents(response.data.data || []);
      } else {
        handleOpenSnackBar("error", response?.data?.message);
      }
    } catch (error) {
      handleOpenSnackBar("error", error?.message);
    }
  };

  const selectedAgents = searchKeys?.leadSelectedAgents || [];
  const saleStatusValue = searchKeys?.leadSaleStatusValue || [];

  return (
    <Stack
      mt={3}
      mb={2}
      px={2}
      py={2}
      gap={1.5}
      bgcolor="primary.light"
      borderRadius={2}
    >
      <Typography variant="body2" fontSize="0.75rem" color="secondary.dark">
        Connections ({allContracts?.length || 0})
      </Typography>

      <Stack
        direction="row"
        alignItems="center"
        gap={1.5}
        flexWrap="wrap"
        useFlexGap
      >
        <Box flex={{ xs: "1 1 100%", sm: "1 1 220px" }} minWidth={200} maxWidth={320}>
          <TextField
            fullWidth
            size="small"
            variant="outlined"
            placeholder="Search..."
            sx={controlStyles}
            value={searchKeys?.leadSearchValue || ""}
            onChange={(e) => dispatch(setLeadSearchValue(e.target.value))}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <img src={SearchIcon} width={16} alt="Search" />
                </InputAdornment>
              ),
              ...(searchKeys?.leadSearchValue && {
                endAdornment: (
                  <InputAdornment position="end">
                    <CancelOutlinedIcon
                      fontSize="small"
                      onClick={() => dispatch(setLeadSearchValue(""))}
                      sx={{ cursor: "pointer" }}
                    />
                  </InputAdornment>
                ),
              }),
            }}
          />
        </Box>

        <Box flex={{ xs: "1 1 100%", sm: "1 1 180px" }} minWidth={180} maxWidth={240}>
          <CustomDropDown
            size="small"
            variant="outlined"
            stylePropos={controlStyles}
            displayEmpty
            multiple
            value={saleStatusValue}
            onChange={(e) => {
              dispatch(setCurrentPage(1));
              dispatch(setLeadSaleStatusValue(e.target.value));
            }}
            renderValue={(selected) => {
              if (!selected?.length) {
                return (
                  <Typography fontSize="0.875rem" color="text.secondary">
                    Sale status
                  </Typography>
                );
              }
              return (
                <Box sx={chipWrapStyle}>
                  {selected.map((val) => (
                    <Chip key={val} size="small" label={val} />
                  ))}
                </Box>
              );
            }}
          >
            {SaleStatus.map((val, index) => (
              <MenuItem key={index} value={val}>
                {val}
              </MenuItem>
            ))}
          </CustomDropDown>
        </Box>

        <Box flex={{ xs: "1 1 100%", sm: "1 1 180px" }} minWidth={180} maxWidth={240}>
          <CustomDropDown
            size="small"
            variant="outlined"
            stylePropos={controlStyles}
            displayEmpty
            multiple
            value={selectedAgents}
            onChange={(e) => {
              dispatch(setCurrentPage(1));
              dispatch(setLeadSelectedAgents(e.target.value));
            }}
            renderValue={(selected) => {
              if (!selected?.length) {
                return (
                  <Typography fontSize="0.875rem" color="text.secondary">
                    Select agents
                  </Typography>
                );
              }
              return (
                <Box sx={chipWrapStyle}>
                  {selected.map((id) => {
                    const agent = allAgents.find((a) => a._id === id);
                    return (
                      <Chip key={id} size="small" label={agent?.username || id} />
                    );
                  })}
                </Box>
              );
            }}
          >
            {allAgents.map((val) => (
              <MenuItem key={val._id} value={val._id}>
                {val.username}
              </MenuItem>
            ))}
          </CustomDropDown>
        </Box>

        <Stack
          direction="row"
          alignItems="center"
          gap={1}
          flex={{ xs: "1 1 100%", sm: "0 1 auto" }}
          flexWrap="wrap"
        >
          <CustomRangePicker
            startDate={searchKeys?.leadStartDate}
            endDate={searchKeys?.leadEndDate}
            setStartDate={(start) => dispatch(setLeadStartDate(start))}
            setEndDate={(end) => dispatch(setLeadEndDate(end))}
            handleFilterDate={onDateFilter}
            stackProps={{
              boxShadow: "none",
              borderRadius: "50px",
              padding: "4px 12px",
              border: "1px solid",
              borderColor: "primary.main",
              bgcolor: "#ffffff",
              minHeight: 40,
            }}
          />
          <GlobalButton
            title="Reset"
            handleClickBtn={handleReset}
            style={{
              width: "auto",
              minWidth: 80,
              height: 40,
              padding: "0 16px",
              borderRadius: "50px",
            }}
          />
        </Stack>
      </Stack>
    </Stack>
  );
};

export default SearchTab;
